import dayjs, { type Dayjs } from 'dayjs';

import { type ClassWithInstructors, type Direction, DirectionOption, type Option } from '../types';

interface FilterByInstructorProps {
    classes: ClassWithInstructors[];
    selectedInstructor: Option | null;
}

export const filterByInstructor = ({ classes, selectedInstructor }: FilterByInstructorProps): ClassWithInstructors[] =>
    classes.filter((classValue) => {
        if (selectedInstructor) {
            const found = classValue.instructors.find((instructor) => instructor.email === selectedInstructor.email);

            return found;
        }
        return true;
    });

interface FilterBySearchProps {
    classes: ClassWithInstructors[];
    searchValue: string;
}

export const filterBySearch = ({ classes, searchValue }: FilterBySearchProps): ClassWithInstructors[] =>
    classes.filter((classValue) => {
        if (searchValue.length < 1) {
            return true;
        }

        const searchTerm = searchValue.trim().toLocaleLowerCase();

        // Using toLocaleLowerCase() for locale-aware case conversion to properly handle
        // international characters and different language-specific casing rules
        const className = classValue?.class_name;
        if (className && className.toLocaleLowerCase().includes(searchTerm)) {
            return true;
        }

        // Search instructor names
        const matchesInstructor = classValue.instructors.some(
            (instructor) =>
                (instructor.user_name ?? '').toLocaleLowerCase().includes(searchTerm) ||
                (instructor.email ?? '').toLocaleLowerCase().includes(searchTerm)
        );
        if (matchesInstructor) {
            return true;
        }

        // Search numeric meeting IDs with shorter length requirement (4+ chars)
        const searchTermIsNumeric = /^\d+$/.test(searchTerm);
        if (searchTermIsNumeric && searchTerm.length >= 4) {
            const meetingId = String(classValue?.meeting_id ?? '');
            if (meetingId.includes(searchTerm)) {
                return true;
            }
        }

        // Search UUIDs only when search term is reasonable length (8+ chars to avoid too many matches)
        if (searchTerm.length >= 8) {
            const classUuid = classValue?.class_uuid ?? '';
            if (classUuid.toLocaleLowerCase().includes(searchTerm)) {
                return true;
            }
        }

        return false;
    });

interface ReturnClassDateStringProps {
    index: 'first' | 'last';
    classDates?: string | null;
}

export const returnClassDateString = ({ classDates, index }: ReturnClassDateStringProps) => {
    if (typeof classDates !== 'string') return null;

    const classDatesArray = classDates.split(',');
    const indexNumber = index === 'first' ? 0 : classDatesArray.length - 1;
    const date = classDatesArray[indexNumber];

    if (!date) return null;

    const lastClassDate = date.split(':')[0];

    if (!lastClassDate) return null;

    return lastClassDate;
};

interface FilterByStartDateProps {
    classes: ClassWithInstructors[];
    startDate: Dayjs | null;
}

export const filterByStartDate = ({ classes, startDate }: FilterByStartDateProps): ClassWithInstructors[] =>
    classes.filter((classValue) => {
        if (!startDate) return true;

        const classStartDateString = classValue.starting_at;

        if (!classStartDateString) {
            return false;
        }

        const classStartDate = dayjs(classStartDateString);

        const isBeforeSelectedDay = startDate.isBefore(classStartDate.toISOString(), 'day');
        const isSameSelectedDay = startDate.isSame(classStartDate.toISOString(), 'day');

        return isBeforeSelectedDay || isSameSelectedDay;
    });

interface FilterByEndDateProps {
    classes: ClassWithInstructors[];
    endDate: Dayjs | null;
}

export const filterByEndDate = ({ classes, endDate }: FilterByEndDateProps): ClassWithInstructors[] =>
    classes.filter((classValue) => {
        if (!endDate) return true;

        const classEndDateString = classValue.ending_at;

        if (!classEndDateString) {
            return false;
        }

        const classEndDate = dayjs(classEndDateString);

        const isAfterSelectedDay = endDate.isAfter(classEndDate.toISOString(), 'day');
        const isSameSelectedDay = endDate.isSame(classEndDate.toISOString(), 'day');

        return isAfterSelectedDay || isSameSelectedDay;
    });

interface FilterByCreatedAfterDateProps {
    classes: ClassWithInstructors[];
    createdAfterDate: Dayjs | null;
}

export const filterByCreatedAfterDate = ({
    classes,
    createdAfterDate
}: FilterByCreatedAfterDateProps): ClassWithInstructors[] =>
    classes.filter((classValue) => {
        if (!createdAfterDate) return true;

        const classCreatedDateString = classValue.created_at;

        if (!classCreatedDateString) {
            return false;
        }

        return dayjs(classCreatedDateString).isSameOrAfter(createdAfterDate, 'day');
    });

interface SortByProps {
    classes: ClassWithInstructors[];
    sort: Direction;
}

export const sortByClassName = ({ classes, sort }: SortByProps): ClassWithInstructors[] => {
    switch (sort) {
        case DirectionOption.ASCENDING:
            return classes.sort((classA, classB) => classA.class_name.trim().localeCompare(classB.class_name.trim()));
        case DirectionOption.DESCENDING:
            return classes.sort((classA, classB) => classB.class_name.trim().localeCompare(classA.class_name.trim()));
        default:
            return classes;
    }
};

export const sortByDates = ({ classes, sort }: SortByProps): ClassWithInstructors[] => {
    switch (sort) {
        case DirectionOption.ASCENDING:
            return classes.sort((a, b) => {
                if (!a.starting_at) return 1;
                if (!b.starting_at) return -1;
                return new Date(a.starting_at).getTime() - new Date(b.starting_at).getTime();
            });
        case DirectionOption.DESCENDING:
            return classes.sort((a, b) => {
                if (!a.starting_at) return -1;
                if (!b.starting_at) return 1;
                return new Date(b.starting_at).getTime() - new Date(a.starting_at).getTime();
            });
        default:
            return classes;
    }
};

const returnInstructorFirstName = (classWithInstructors: ClassWithInstructors): string | undefined | null =>
    classWithInstructors?.instructors[0]?.user_name;

export const sortByInstructors = ({ classes, sort }: SortByProps): ClassWithInstructors[] => {
    switch (sort) {
        case DirectionOption.ASCENDING:
            return classes.sort((classA, classB) => {
                const instructorNameA = returnInstructorFirstName(classA);
                const instructorNameB = returnInstructorFirstName(classB);

                if (instructorNameA && instructorNameB) {
                    return instructorNameA.trim().localeCompare(instructorNameB.trim());
                }

                return instructorNameA ? -1 : 1;
            });
        case DirectionOption.DESCENDING:
            return classes.sort((classA, classB) => {
                const instructorNameA = returnInstructorFirstName(classA);
                const instructorNameB = returnInstructorFirstName(classB);

                if (instructorNameA && instructorNameB) {
                    return instructorNameB.trim().localeCompare(instructorNameA.trim());
                }

                return instructorNameA ? -1 : 1;
            });
        default:
            return classes;
    }
};

export const sortByCreated = ({ classes, sort }: SortByProps): ClassWithInstructors[] => {
    switch (sort) {
        case DirectionOption.ASCENDING:
            return classes.sort((a, b) => {
                // Place records without created_at at the end
                if (!a.created_at) return 1;
                if (!b.created_at) return -1;

                const timeA = new Date(a.created_at).getTime();
                const timeB = new Date(b.created_at).getTime();

                // Handle invalid dates - place them at the end
                if (isNaN(timeA) && isNaN(timeB)) return 0;
                if (isNaN(timeA)) return 1;
                if (isNaN(timeB)) return -1;

                return timeA - timeB;
            });
        case DirectionOption.DESCENDING:
            return classes.sort((a, b) => {
                // Place records without created_at at the end
                if (!a.created_at) return 1;
                if (!b.created_at) return -1;

                const timeA = new Date(a.created_at).getTime();
                const timeB = new Date(b.created_at).getTime();

                // Handle invalid dates - place them at the end
                if (isNaN(timeA) && isNaN(timeB)) return 0;
                if (isNaN(timeA)) return 1;
                if (isNaN(timeB)) return -1;

                return timeB - timeA;
            });
        default:
            return classes;
    }
};
